import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const TemplatePickerContainerGalleryDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"TemplatePickerContainerGallery"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"boardIds"}},"type":{"kind":"NonNullType","type":{"kind":"ListType","type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"boards"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"ids"},"value":{"kind":"Variable","name":{"kind":"Name","value":"boardIds"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"dateLastActivity"}},{"kind":"Field","name":{"kind":"Name","value":"dateLastView"}},{"kind":"Field","name":{"kind":"Name","value":"idOrganization"}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"backgroundColor"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundImage"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundImageScaled"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"height"}},{"kind":"Field","name":{"kind":"Name","value":"url"}},{"kind":"Field","name":{"kind":"Name","value":"width"}}]}},{"kind":"Field","name":{"kind":"Name","value":"isTemplate"}}]}},{"kind":"Field","name":{"kind":"Name","value":"shortLink"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"TemplatePickerContainerGallery","document":TemplatePickerContainerGalleryDocument}} as const;
export type TemplatePickerContainerGalleryQueryVariables = Types.Exact<{
  boardIds: Array<Types.Scalars['ID']['input']> | Types.Scalars['ID']['input'];
}>;


export type TemplatePickerContainerGalleryQuery = (
  { __typename: 'Query' }
  & { boards: Array<(
    { __typename: 'Board' }
    & Pick<Types.Board, 'id' | 'dateLastActivity' | 'dateLastView' | 'idOrganization' | 'name' | 'shortLink'>
    & { prefs?: Types.Maybe<(
      { __typename: 'Board_Prefs' }
      & Pick<Types.Board_Prefs, 'backgroundColor' | 'backgroundImage' | 'isTemplate'>
      & { backgroundImageScaled?: Types.Maybe<Array<(
        { __typename: 'Board_Prefs_BackgroundImageScaled' }
        & Pick<Types.Board_Prefs_BackgroundImageScaled, 'height' | 'url' | 'width'>
      )>> }
    )> }
  )> }
);

/**
 * __useTemplatePickerContainerGalleryQuery__
 *
 * To run a query within a React component, call `useTemplatePickerContainerGalleryQuery` and pass it any options that fit your needs.
 * When your component renders, `useTemplatePickerContainerGalleryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTemplatePickerContainerGalleryQuery({
 *   variables: {
 *      boardIds: // value for 'boardIds'
 *   },
 * });
 */
export function useTemplatePickerContainerGalleryQuery(
  baseOptions: TrelloQueryHookOptions<
    TemplatePickerContainerGalleryQuery,
    TemplatePickerContainerGalleryQueryVariables
  > &
    (
      | {
          variables: TemplatePickerContainerGalleryQueryVariables;
          skip?: boolean;
        }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: TemplatePickerContainerGalleryDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    TemplatePickerContainerGalleryQuery,
    TemplatePickerContainerGalleryQueryVariables
  >(TemplatePickerContainerGalleryDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useTemplatePickerContainerGalleryLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    TemplatePickerContainerGalleryQuery,
    TemplatePickerContainerGalleryQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    TemplatePickerContainerGalleryQuery,
    TemplatePickerContainerGalleryQueryVariables
  >(TemplatePickerContainerGalleryDocument, options);
}
export function useTemplatePickerContainerGallerySuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        TemplatePickerContainerGalleryQuery,
        TemplatePickerContainerGalleryQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    TemplatePickerContainerGalleryQuery,
    TemplatePickerContainerGalleryQueryVariables
  >(TemplatePickerContainerGalleryDocument, options);
}
export type TemplatePickerContainerGalleryQueryHookResult = ReturnType<
  typeof useTemplatePickerContainerGalleryQuery
>;
export type TemplatePickerContainerGalleryLazyQueryHookResult = ReturnType<
  typeof useTemplatePickerContainerGalleryLazyQuery
>;
export type TemplatePickerContainerGallerySuspenseQueryHookResult = ReturnType<
  typeof useTemplatePickerContainerGallerySuspenseQuery
>;
export type TemplatePickerContainerGalleryQueryResult = Apollo.QueryResult<
  TemplatePickerContainerGalleryQuery,
  TemplatePickerContainerGalleryQueryVariables
>;
